import React, { useEffect, useState } from "react";
import { api, dateFormat, euroFormat, formatApiErrorDetail } from "../../lib/api";
import { Download } from "lucide-react";
import ExportButton from "../../components/ExportButton";

export default function Gutschriften() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [showBlanko, setShowBlanko] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => api.get("/admin/gutschriften").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/admin/users", { params: { role: "kunde" } }).then((r) => setUsers(r.data));
  }, []);

  const storno = async (gid) => {
    if (!window.confirm("Wirklich stornieren? Es wird ein neues Stornodokument erstellt.")) return;
    try {
      const r = await api.post(`/admin/gutschriften/${gid}/storno`);
      setMsg(`Storno ${r.data.storno_nummer} erstellt.`);
      load();
    } catch (e) {
      setMsg(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5" data-testid="gutschriften-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-heading font-bold text-falke-blue">Gutschriften</h1>
        <div className="flex gap-2">
          <ExportButton endpoint="/admin/exports/gutschriften" defaultFilename="Gutschriften.csv" label="Gutschriften-Export" testId="export-gutschriften" />
          <button onClick={() => setShowBlanko(true)} className="btn-primary" data-testid="open-blanko">+ Neue Blanko-Gutschrift</button>
        </div>
      </div>
      {msg && <div className="text-sm text-falke-success" data-testid="gs-msg">{msg}</div>}

      <div className="falke-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-falke-blue/70 text-xs uppercase tracking-wider border-b border-falke-blue/10">
            <tr><th className="px-4 py-3">Nummer</th><th>Datum</th><th>Empfänger</th><th>Brutto</th><th>Status</th><th>Aktion</th></tr>
          </thead>
          <tbody>
            {items.map((g) => (
              <tr key={g.id} className="border-t border-falke-blue/5" data-testid={`gs-${g.id}`}>
                <td className="px-4 py-3 font-semibold">{g.gutschriftnummer}{g.ist_blanko && <span className="text-xs text-falke-orange ml-2">Blanko</span>}</td>
                <td>{dateFormat(g.ausstellungsdatum)}</td>
                <td>{g.empfaenger_name}</td>
                <td className="tabular font-bold">{euroFormat(g.summe_brutto)}</td>
                <td><span className={`status-badge ${g.status === "storniert" ? "bg-rose-100 text-rose-800" : g.status === "widersprochen" ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"}`}>{g.status}</span></td>
                <td className="space-x-2">
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/gutschriften/${g.id}/pdf`} target="_blank" rel="noreferrer" className="text-falke-orange font-semibold inline-flex items-center gap-1"><Download size={14} /> PDF</a>
                  {g.status !== "storniert" && <button onClick={() => storno(g.id)} className="text-rose-600 text-sm hover:underline" data-testid={`storno-${g.id}`}>Storno</button>}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-falke-anthracite/60">Noch keine Gutschriften.</td></tr>}
          </tbody>
        </table>
      </div>

      {showBlanko && <BlankoModal users={users} onClose={() => setShowBlanko(false)} onCreated={() => { setShowBlanko(false); load(); }} />}
    </div>
  );
}

function BlankoModal({ users, onClose, onCreated }) {
  const [empfaenger, setEmpfaenger] = useState("");
  const [positionen, setPositionen] = useState([{ beschreibung: "", menge: 1, einheit: "Pauschal", einzelpreis_netto: 0, ust_satz: 19 }]);
  const [von, setVon] = useState(new Date().toISOString().slice(0, 10));
  const [bis, setBis] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    try {
      const r = await api.post("/admin/gutschriften/blanko", {
        empfaenger_id: empfaenger,
        positionen,
        leistungszeitraum_von: von,
        leistungszeitraum_bis: bis,
      });
      onCreated();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const updatePos = (i, field, val) => {
    const next = [...positionen];
    next[i] = { ...next[i], [field]: field === "beschreibung" || field === "einheit" ? val : Number(val) };
    setPositionen(next);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="blanko-modal">
      <div className="bg-white max-w-2xl w-full rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="font-heading text-2xl text-falke-blue mb-4">Neue Blanko-Gutschrift</h3>
        <label className="block text-sm font-medium text-falke-blue mb-1.5">Empfänger</label>
        <select value={empfaenger} onChange={(e) => setEmpfaenger(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-falke-blue/15 mb-4" data-testid="blanko-empfaenger">
          <option value="">— wählen —</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
        </select>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div><label className="block text-sm text-falke-blue mb-1">Leistung von</label><input type="date" value={von} onChange={(e) => setVon(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-falke-blue/15" /></div>
          <div><label className="block text-sm text-falke-blue mb-1">Leistung bis</label><input type="date" value={bis} onChange={(e) => setBis(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-falke-blue/15" /></div>
        </div>

        <div className="text-sm font-medium text-falke-blue mb-2">Positionen</div>
        {positionen.map((p, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 mb-2">
            <input placeholder="Beschreibung" value={p.beschreibung} onChange={(e) => updatePos(i, "beschreibung", e.target.value)} className="col-span-5 px-2 py-2 rounded-lg border border-falke-blue/15 text-sm" data-testid={`pos-besch-${i}`} />
            <input type="number" placeholder="Menge" value={p.menge} onChange={(e) => updatePos(i, "menge", e.target.value)} className="col-span-1 px-2 py-2 rounded-lg border border-falke-blue/15 text-sm" />
            <input placeholder="Einheit" value={p.einheit} onChange={(e) => updatePos(i, "einheit", e.target.value)} className="col-span-2 px-2 py-2 rounded-lg border border-falke-blue/15 text-sm" />
            <input type="number" step="0.01" placeholder="Einzelpreis netto" value={p.einzelpreis_netto} onChange={(e) => updatePos(i, "einzelpreis_netto", e.target.value)} className="col-span-2 px-2 py-2 rounded-lg border border-falke-blue/15 text-sm" />
            <input type="number" placeholder="USt %" value={p.ust_satz} onChange={(e) => updatePos(i, "ust_satz", e.target.value)} className="col-span-2 px-2 py-2 rounded-lg border border-falke-blue/15 text-sm" />
          </div>
        ))}
        <button onClick={() => setPositionen([...positionen, { beschreibung: "", menge: 1, einheit: "Pauschal", einzelpreis_netto: 0, ust_satz: 19 }])} className="text-sm text-falke-orange font-semibold" data-testid="add-pos">+ Position</button>

        {error && <div className="text-sm text-red-600 mt-3">{error}</div>}

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn-outline">Abbrechen</button>
          <button onClick={submit} disabled={!empfaenger} className="btn-primary" data-testid="blanko-submit">Erstellen</button>
        </div>
      </div>
    </div>
  );
}
